import { useState } from "react";
import { useRecoilState } from "recoil";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { useNavigate } from "react-router-dom";
import {
  axiosInstancePromotion,
  CustomResponseFormat,
  setBearerTokenPromotion,
} from "api/instancePromotion";
import { trackCapiStandardEvent } from "analytics/metaCapi";
import mixpanel from "mixpanel-browser";
import useMixpanelProfileMkt from "./useMixpanelProfileMkt";
import useMixpanelMKT from "./useMixpanelMKT";
import useGtagConversion from "./useGtagConversion";
import { useMutation } from "@tanstack/react-query";
import { webFunnelApi } from "../api/webFunnelApi";
import { captureException } from "../utils/sentry";
import { PATH } from "../routes/paths";

export default function useLoginPromotion({
  url,
  idToken,
  code,
  type,
  email,
  redirectUri,
}: {
  url: string;
  idToken?: string;
  code?: string;
  type: "google" | "apple" | "kakao";
  email?: string;
  redirectUri?: string;
}) {
  let navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [user, setUser] = useRecoilState(userSimpleAtom);
  const setPromotionPeople = useMixpanelProfileMkt("v4");

  const trackGtagEvent = useGtagConversion();

  const trackMixpanelRegistration = useMixpanelMKT({
    params: {
      method: type,
    },
    eventName: "가입",
    version: "v4",
  });

  const trackMixpanelLogin = useMixpanelMKT({
    params: {
      method: type,
    },
    eventName: "로그인",
    version: "v4",
  });

  const { mutate: identify } = useMutation(
    ({ distinctId }: { distinctId: string }) =>
      webFunnelApi.identify(distinctId),
    {
      onError: (error, variables) => {
        captureException(error, {
          tags: { action: "web_funnel_identify" },
          extra: { distinctId: variables.distinctId },
        });
      },
    }
  );

  const login = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(undefined);
    try {
      const response = await axiosInstancePromotion.post<CustomResponseFormat>(
        url,
        {
          type: type,
          ...(idToken && { idToken: idToken }),
          ...(code && { code: code }),
          ...(email && { email: email }),
          ...(redirectUri && { redirectUri: redirectUri }),
        }
      );

      if (response.code === 0) {
        const result = response.result;
        setBearerTokenPromotion(result.accessToken);
        setUser(result);

        const distinctId =
          localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ||
          (mixpanel as any).promotion?.get_distinct_id();

        if (distinctId) {
          identify({ distinctId: distinctId });
        }

        if (result.isFirstLogin) {
          // Track CompleteRegistration event with userId and email
          trackCapiStandardEvent("CompleteRegistration", {
            value: 0,
            currency: "KRW",
            externalId: result.userId,
            email: result.email,
          });

          trackGtagEvent("CompleteRegistration", "v4", {
            email: result.email,
          });

          trackMixpanelRegistration();
        } else {
          trackMixpanelLogin();
        }

        const promotionInstance = (mixpanel as any).promotion;
        if (promotionInstance && result.trackId) {
          setPromotionPeople({
            $email: result.email,
            $user_id: result.userId,
            ...(result.nickname && { $name: result.nickname }),
            ...(result.paymentPageType && {
              "결제 페이지 유형": result.paymentPageType.toUpperCase(),
            }),
          });
        }

        if (result.isFirstLogin) {
          navigate("/promotion-onboarding/nickname/1", { replace: true });
        } else {
          if (result?.hasAlreadyUsed) {
            navigate(PATH.promotionAlreadyUsed, { replace: true });
          } else {
            navigate("/promotion-onboarding/auth-loading/1", { replace: true });
          }
        }
      } else {
        setError(`로그인에 실패했습니다. 다시 시도해주세요.: ${response.code}`);
      }
    } catch (error) {
      console.error("Login failed:", error);
      if (!error) {
        setError(`로그인에 실패했습니다. 다시 시도해주세요. ${error}`);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { login, isLoading, error };
}
